import { Box, ButtonSkin, Text, TextButton } from '@wix/design-system';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContextProvider } from '../../AppLoader';
import { ButtonWithLoader } from '../../ButtonWithLoader/ButtonWithLoader';
import { DialogHeader } from '../../DialogHeader/DialogHeader';
import { MainHeader } from '../../MainHeader';
import { ThemedText } from '../../ThemedComponents';
import { dataHooks } from '../../../dataHooks';
import PasskeyEnrollIllustration from '../../../assets/images/passkeyEnrollIllustration.svg';
import PasskeySuccessIllustration from '../../../assets/images/passkeySuccessIllustration.svg';
import PasskeyIcon from '../../../assets/images/passkeyIcon.svg';
import SsoIcon from '../../../assets/images/ssoIcon.svg';
import { Confirm } from '@wix/wix-ui-icons-common';
import {
  passkeyConnectionsSucceedSrc5Evid5022,
  postLoginCtaClickSrc5Evid6001,
  postLoginErrorSrc5Evid6002,
} from '@wix/bi-logger-identity-data/v2';
import { PASSKEY_ENROLL_KEYS } from './keys';
import { PasskeyEnrollError } from './PasskeyEnrollError';
import { StepUpModal } from '../StepUpModal/StepUpModal';
import type { PasskeyEnrollmentStatus } from '../../../stores/passkeyEnroll';

const STEP_UP_CLOSE_CTA = 'step-up close';

const getPasskeyLearnMoreUrl = (locale: string) =>
  `https://support.wix.com/${locale}/article/using-a-passkey-to-log-in-to-your-wix-account`;

interface EnrollButtonProps {
  enrollmentStatus: PasskeyEnrollmentStatus;
  buttonText: string;
  skin: ButtonSkin;
  showArrowOnOver: boolean;
  showLoader: boolean;
  onClick: () => void;
}

const EnrollButton: React.FC<EnrollButtonProps> = ({
  enrollmentStatus,
  buttonText,
  skin,
  showArrowOnOver,
  showLoader,
  onClick,
}) => {
  return (
    <Box
      width="fit-content"
      minWidth={enrollmentStatus === 'success' ? undefined : '200px'}
    >
      <ButtonWithLoader
        dataHook={dataHooks.postLogin.passkeyEnroll.button}
        priority="primary"
        skin={skin}
        prefixIcon={
          enrollmentStatus !== 'enrolling' && enrollmentStatus !== 'success' ? (
            <PasskeyIcon />
          ) : undefined
        }
        showArrowOnOver={showArrowOnOver}
        onClick={onClick}
        showLoader={showLoader}
      >
        {buttonText}
      </ButtonWithLoader>
    </Box>
  );
};

const PostLoginPasskeyEnroll: React.FC = observer(() => {
  const { rootStore } = useContext(AppContextProvider);
  const { postLoginStore, passkeyEnrollStore, displayStore, biLogger } =
    rootStore;
  const { preset } = displayStore;
  const { t } = useTranslation();
  const [isProceedingToPostAuth, setIsProceedingToPostAuth] = useState(false);

  useEffect(() => {
    if (postLoginStore.postLoginOffer?.offerGuid) {
      rootStore.dealerApi.sendDealerViewEvent(
        postLoginStore.postLoginOffer.offerGuid,
      );
    }
  }, []);

  const enrollmentStatus = passkeyEnrollStore.enrollmentStatus;
  const isEnrollCompleted = enrollmentStatus === 'success';
  const { common, success } = PASSKEY_ENROLL_KEYS;

  const titleKey = isEnrollCompleted ? success.title : common.title;
  const subtitleKey = isEnrollCompleted ? success.subtitle : common.subtitle;
  const buttonKey = isEnrollCompleted ? success.button : common.button;

  const handleButtonClick = async () => {
    if (isEnrollCompleted) {
      setIsProceedingToPostAuth(true);
      postLoginStore.offerSucceed('secondary');
      return;
    }
    if (enrollmentStatus === 'enrolling') {
      return;
    }
    if (enrollmentStatus === 'error') {
      passkeyEnrollStore.reset();
    }
    postLoginStore.reportMainCtaClick();
    const result = await passkeyEnrollStore.enrollPasskey();
    if (result.success) {
      biLogger.report(
        passkeyConnectionsSucceedSrc5Evid5022({
          origin: 'post login - speed up your login',
        }),
      );
    } else if (result.cancelled) {
      // Step-up modal dismissed: not an error, so no error UI or error BI.
      biLogger.report(
        postLoginCtaClickSrc5Evid6001({
          post_login_offer: 'passkey',
          flow: 'speed up your login',
          cta_type: STEP_UP_CLOSE_CTA,
        }),
      );
    } else {
      biLogger.report(
        postLoginErrorSrc5Evid6002({
          post_login_offer: 'passkey',
          flow: 'speed up your login',
          error_name: result.errorCode,
          error_message: result.errorMessage,
        }),
      );
    }
  };

  const benefitItems = [
    t(common.howItWorksItemNoPasswords),
    t(common.howItWorksItemPhishing),
    t(common.howItWorksItemSynced),
  ];

  const subtitleNode = isEnrollCompleted ? (
    <DialogHeader.SubTitleSmall>{t(subtitleKey)}</DialogHeader.SubTitleSmall>
  ) : (
    <Box direction="vertical" gap={4} maxWidth="360px">
      <Box direction="vertical" align="left">
        <DialogHeader.SubTitleSmall>
          {t(subtitleKey)}
        </DialogHeader.SubTitleSmall>
        <Box width="fit-content">
          <TextButton
            dataHook={dataHooks.postLogin.passkeyEnroll.learnMore}
            as="a"
            href={getPasskeyLearnMoreUrl(rootStore.language.locale)}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            skin="standard"
            underline="always"
            style={{ color: 'var(--wds-button-fill-standard-primary)' }}
          >
            {t(common.learnMore)}
          </TextButton>
        </Box>
      </Box>
      <Box
        direction="vertical"
        gap={2}
        dataHook={dataHooks.postLogin.passkeyEnroll.howItWorks}
      >
        <Text size="small" weight="thin">
          {t(common.howItWorksTitle)}
        </Text>
        <Box direction="vertical" gap={1}>
          {benefitItems.map((item, index) => (
            <Box
              key={index}
              direction="horizontal"
              gap={2}
              verticalAlign="middle"
            >
              <Confirm size="20px" />
              <Text size="small" secondary>
                {item}
              </Text>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  const contentColumn = (
    <Box direction="vertical" maxWidth="420px">
      <DialogHeader.Title flow="authPage">{t(titleKey)}</DialogHeader.Title>
      <Box marginTop="SP2">{subtitleNode}</Box>
      <Box marginTop="SP7" direction="vertical" gap="SP4" align="left">
        <Box direction="horizontal" gap="SP4" verticalAlign="middle">
          <EnrollButton
            enrollmentStatus={enrollmentStatus}
            buttonText={t(buttonKey)}
            skin={preset.buttonDesign?.skin ?? 'standard'}
            showArrowOnOver={preset.buttonDesign?.showArrowOnButton ?? false}
            showLoader={
              enrollmentStatus === 'enrolling' || isProceedingToPostAuth
            }
            onClick={handleButtonClick}
          />
          {!isEnrollCompleted && (
            <TextButton
              dataHook={dataHooks.postLogin.common.skip}
              size="small"
              weight="thin"
              skin="dark"
              underline="always"
              onClick={() => postLoginStore.showLater()}
            >
              {t(common.skipCta)}
            </TextButton>
          )}
        </Box>
        {!isEnrollCompleted && (
          <Box
            direction="horizontal"
            gap="SP1"
            verticalAlign="middle"
            dataHook={dataHooks.postLogin.passkeyEnroll.passwordFallbackHint}
          >
            <SsoIcon />
            <ThemedText size="tiny" weight="thin" secondary>
              {t(common.passwordFallbackHint)}
            </ThemedText>
          </Box>
        )}
        {enrollmentStatus === 'error' && passkeyEnrollStore.errorCode && (
          <PasskeyEnrollError
            errorCode={passkeyEnrollStore.errorCode}
            onClose={() => passkeyEnrollStore.reset()}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      direction="vertical"
      width="100%"
      minHeight="100vh"
      backgroundColor="WHITE"
    >
      <MainHeader showBackButton={false} disableLogoClick={false} />
      <StepUpModal />
      <Box
        direction="vertical"
        flex={1}
        verticalAlign="middle"
        paddingLeft="SP9"
        paddingRight="SP9"
      >
        <Box
          direction="horizontal"
          verticalAlign="middle"
          gap="SP9"
          width="fit-content"
          marginLeft="auto"
          marginRight="auto"
        >
          {contentColumn}
          <Box verticalAlign="middle">
            {enrollmentStatus === 'success' ? (
              <PasskeySuccessIllustration />
            ) : (
              <PasskeyEnrollIllustration />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default PostLoginPasskeyEnroll;
